import { useEffect, useState } from 'react'

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string
const ADMIN_PASSWORD = 'scenic-admin-2024'

interface Review {
  id: string
  guest_name: string
  city: string
  quote: string
  rating: number
  created_at: string
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="text-sm" style={{ color: '#2d5a3d' }}>
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </span>
  )
}

export default function AdminReviews() {
  const [authed, setAuthed] = useState(false)
  const [password, setPassword] = useState('')
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(false)
  const [actionId, setActionId] = useState<string | null>(null)

  async function fetchPending() {
    setLoading(true)
    try {
      const res = await fetch(`${BASE_URL}/api/admin/reviews`, {
        headers: { 'x-admin-password': ADMIN_PASSWORD },
      })
      const data = await res.json() as { success: boolean; data: Review[] }
      if (data.success) setReviews(data.data)
    } catch {
      // API not reachable — Worker not deployed yet
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (authed) fetchPending() }, [authed])

  async function approve(id: string) {
    setActionId(id)
    await fetch(`${BASE_URL}/api/admin/reviews/${id}/approve`, {
      method: 'PATCH',
      headers: { 'x-admin-password': ADMIN_PASSWORD },
    })
    setReviews(r => r.filter(rev => rev.id !== id))
    setActionId(null)
  }

  async function reject(id: string) {
    setActionId(id)
    await fetch(`${BASE_URL}/api/admin/reviews/${id}`, {
      method: 'DELETE',
      headers: { 'x-admin-password': ADMIN_PASSWORD },
    })
    setReviews(r => r.filter(rev => rev.id !== id))
    setActionId(null)
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-8">
        <div className="w-full max-w-sm">
          <p className="text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-6 text-center">Scenic Solitude · Admin</p>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password"
            className="w-full border border-cream-300 bg-cream-50 px-4 py-3 text-sm text-ink focus:outline-none focus:border-ink/40 mb-4"
            onKeyDown={e => { if (e.key === 'Enter' && password === ADMIN_PASSWORD) setAuthed(true) }}
          />
          <button
            onClick={() => { if (password === ADMIN_PASSWORD) setAuthed(true) }}
            className="w-full border border-ink px-6 py-3 text-[11px] tracking-widest2 uppercase text-ink hover:bg-ink hover:text-cream-50 transition-all duration-200"
          >
            Enter
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-cream-100">
      <div className="mx-auto max-w-3xl px-8 py-16">

        <div className="flex items-baseline justify-between mb-12">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">Scenic Solitude · Admin</p>
            <h1 className="text-2xl font-semibold text-ink">Pending Reviews</h1>
          </div>
          <button onClick={fetchPending} className="text-xs text-ink-muted hover:text-ink transition-colors">
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-ink-soft">Loading…</p>
        ) : reviews.length === 0 ? (
          <div className="border border-cream-300 px-8 py-12 text-center">
            <p className="text-sm text-ink-soft">No pending reviews. All caught up.</p>
          </div>
        ) : (
          <div className="space-y-px">
            {reviews.map(review => (
              <div key={review.id} className="bg-cream-50 border border-cream-300 px-6 py-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-semibold text-ink text-sm">{review.guest_name}</p>
                      <span className="text-[10px] tracking-widest text-ink-muted uppercase">{review.city}</span>
                      <Stars rating={review.rating} />
                    </div>
                    <p className="text-sm text-ink-soft font-light leading-relaxed mt-2">"{review.quote}"</p>
                    <p className="text-[10px] text-ink-light mt-3">{new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => approve(review.id)}
                      disabled={actionId === review.id}
                      className="px-4 py-1.5 text-[10px] tracking-widest uppercase border border-[#2d5a3d] text-[#2d5a3d] hover:bg-[#2d5a3d] hover:text-white transition-all duration-200 disabled:opacity-40"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => reject(review.id)}
                      disabled={actionId === review.id}
                      className="px-4 py-1.5 text-[10px] tracking-widest uppercase border border-red-300 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-200 disabled:opacity-40"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
